/**
 * 💰 Price Utilities for Sales Management
 * Strategy: round internally at 4 decimals to reduce floating point accumulation,
 * then display with 3 decimals (bankers rounding).
 */
/* ------------------------------
 * 🔹 CORE UTILS - internal precision (4 decimals)
 * ------------------------------ */

/**
 * Internal rounding to 3 decimal places (IEEE-safe)
 * Used for intermediate arithmetic to reduce cumulative floating errors.
 */
export const roundInternalTo3 = (num) =>
  Math.round((parseFloat(num) + Number.EPSILON) * 1000) / 1000;

/**
 * Parses and normalizes a value into a number with internal 3-decimal precision
 */
export const parsePrice = (value) => {
  if (value == null || value === "") return 0;

  const num =
    typeof value === "number"
      ? value
      : parseFloat(String(value).replace(/\s/g, "").replace(",", "."));

  if (isNaN(num)) return 0;

  return roundInternalTo3(num);
};

/* ------------------------------
 * 🔹 SAFE ARITHMETIC using internal 4-decimals
 * ------------------------------ */

export const addMoney = (a, b) =>
  roundInternalTo3(parsePrice(a) + parsePrice(b));

export const subMoney = (a, b) =>
  roundInternalTo3(parsePrice(a) - parsePrice(b));

export const mulMoney = (a, b) =>
  roundInternalTo3(parsePrice(a) * parsePrice(b));

export const divMoney = (a, b) =>
  b === 0 ? 0 : roundInternalTo3(parsePrice(a) / parsePrice(b));

/* ------------------------------
 * 🔹 BANKERS ROUNDING FOR DISPLAY (3 decimals)
 * ------------------------------ */

/**
 * Bankers rounding (financial rounding) to N decimals.
 * Rounds to the nearest even when exactly halfway.
 */
export const roundBankers = (value, decimals = 3) => {
  const factor = Math.pow(10, decimals);
  const n = value * factor;
  const f = Math.floor(n);
  const diff = n - f;
  if (diff > 0.5) return (f + 1) / factor;
  if (diff < 0.5) return f / factor;
  // exactly .5 -> round to nearest even
  return (f % 2 === 0 ? f : f + 1) / factor;
};

/* ------------------------------s
 * 🔹 DISPLAY FORMATTING
 * ------------------------------ */

/**
 * Formats a price for display in TND (fr-FR locale), shows 3 decimals.
 * Uses bankers rounding at the final display step.
 */
export const formatPrice = (value, currency = null) => {
  if (value == null || value === "") {
    const decimals = currency?.decimalPlaces ?? 3;

    return (0).toLocaleString("fr-FR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  const decimals = currency?.decimalPlaces ?? 3;

  const roundedDisplay =
    Math.round(
      (parsePrice(value) + Number.EPSILON) *
      Math.pow(10, decimals)
    ) / Math.pow(10, decimals);

  return roundedDisplay.toLocaleString("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};


/* ------------------------------
 * 🔹 CALCULATION FUNCTIONS (use internal 4-decimals)
 * ------------------------------ */

/**
 * Calculate line total excluding tax
 * Formula: quantity × unitPrice × (1 - discount%)
 */
export const calculateLineTotalHT = (
  quantity,
  unitPrice,
  discountPercent = 0
) => {
  const qty = parsePrice(quantity); // 4-decimals
  const price = parsePrice(unitPrice);
  const discount = parsePrice(discountPercent);

  const totalBeforeDiscount = mulMoney(qty, price); // 4-decimals
  const discountAmount = mulMoney(totalBeforeDiscount, discount / 100);

  return subMoney(totalBeforeDiscount, discountAmount); // 4-decimals
};

/**
 * Calculate line discount amount
 */
export const calculateLineDiscountAmount = (
  quantity,
  unitPrice,
  discountPercent = 0
) => {
  const qty = parsePrice(quantity);
  const price = parsePrice(unitPrice);
  const discount = parsePrice(discountPercent);

  const totalBeforeDiscount = mulMoney(qty, price);
  return mulMoney(totalBeforeDiscount, discount / 100);
};

/**
 * Calculate tax amount
 */
export const calculateLineTaxAmount = (totalHT, taxRate) => {
  const total = parsePrice(totalHT);
  const rate = parsePrice(taxRate);
  return mulMoney(total, rate / 100);
};

/**
 * Calculate line total including tax
 */
export const calculateLineTotalTTC = (totalHT, taxAmount) =>
  addMoney(totalHT, taxAmount);

/* ------------------------------
 * 🔹 SALE TOTALS
 * ------------------------------ */

export const calculateSaleTotals = (
  saleLines,
  isTVAActive = true,
  contractDiscountPercent = 0
) => {
  let totalHT = 0;
  let totalTaxAmount = 0;
  let totalLineDiscountAmount = 0;

  saleLines.forEach((line) => {
    if (!line.productId) return;

    const quantity = parsePrice(line.quantity);
    const unitPrice = parsePrice(line.unitPrice);
    const lineDiscountPercent =
      contractDiscountPercent > 0
        ? parsePrice(contractDiscountPercent)
        : parsePrice(line.discountPercent || 0);
    const taxRate = isTVAActive ? parsePrice(line.taxRate || 0) : 0;

    const lineDiscountAmount = calculateLineDiscountAmount(
      quantity,
      unitPrice,
      lineDiscountPercent
    );
    const lineTotalHT = calculateLineTotalHT(
      quantity,
      unitPrice,
      lineDiscountPercent
    );
    const lineTaxAmount = calculateLineTaxAmount(lineTotalHT, taxRate);

    totalHT = addMoney(totalHT, lineTotalHT);
    totalTaxAmount = addMoney(totalTaxAmount, lineTaxAmount);
    totalLineDiscountAmount = addMoney(
      totalLineDiscountAmount,
      lineDiscountAmount
    );
  });

  const totalTTC = addMoney(totalHT, totalTaxAmount);

  return {
    totalHT: parsePrice(totalHT), // internal 4-decimals
    totalTaxAmount: parsePrice(totalTaxAmount),
    totalDiscountAmount: parsePrice(totalLineDiscountAmount),
    totalTTC: parsePrice(totalTTC),
  };
};


/* ------------------------------
 * 🔹 Invoice TOTALS
 * ------------------------------ */

export const calculateInvoiceTotals = (
  saleLines,
  isTVAActive = true,
  isFiscalTmbreActive,
  contractDiscountPercent = 0
) => {
  let totalHT = 0;
  let totalTaxAmount = 0;
  let totalLineDiscountAmount = 0;

  saleLines.forEach((line) => {
    if (!line.productId) return;

    const quantity = parsePrice(line.quantity);
    const unitPrice = parsePrice(line.unitPrice);
    const lineDiscountPercent =
      contractDiscountPercent > 0
        ? parsePrice(contractDiscountPercent)
        : parsePrice(line.discountPercent || 0);
    const taxRate = isTVAActive ? parsePrice(line.taxRate || 0) : 0;

    const lineDiscountAmount = calculateLineDiscountAmount(
      quantity,
      unitPrice,
      lineDiscountPercent
    );
    const lineTotalHT = calculateLineTotalHT(
      quantity,
      unitPrice,
      lineDiscountPercent
    );
    const lineTaxAmount = calculateLineTaxAmount(lineTotalHT, taxRate);

    totalHT = addMoney(totalHT, lineTotalHT);
    totalTaxAmount = addMoney(totalTaxAmount, lineTaxAmount);
    // if(isFiscalTmbreActive){
    //   totalTaxAmount = addMoney(totalTaxAmount, 1);
    // }

    totalLineDiscountAmount = addMoney(
      totalLineDiscountAmount,
      lineDiscountAmount
    );
  });

  const totalTTC = addMoney(totalHT, totalTaxAmount);

  return {
    totalHT: parsePrice(totalHT), // internal 4-decimals
    totalTaxAmount: parsePrice(totalTaxAmount),
    totalDiscountAmount: parsePrice(totalLineDiscountAmount),
    totalTTC: parsePrice(totalTTC),
  };
};

/* ------------------------------
 * 🔹 Purchase total
 * ------------------------------ */
export const calculatePurchaseTotals = (
  purchaseLines,
  isTVAActive = true
) => {
  let totalHT = 0;
  let totalTaxAmount = 0;
  let totalLineDiscountAmount = 0;

  purchaseLines.forEach((line) => {
    if (!line.productId) return;

    const quantity = parsePrice(line.quantity);
    const unitPrice = parsePrice(line.totalLineIncludingTax / quantity);
    const lineDiscountPercent = parsePrice(line.totalLineDiscountPercent || 0);
    const taxRate = isTVAActive ? parsePrice(line.taxRate || 0) : 0;

    const lineDiscountAmount = calculateLineDiscountAmount(
      quantity,
      unitPrice,
      lineDiscountPercent
    );
    const lineTotalHT = calculateLineTotalHT(
      quantity,
      unitPrice,
      lineDiscountPercent
    );
    const lineTaxAmount = calculateLineTaxAmount(lineTotalHT, taxRate);

    totalHT = addMoney(totalHT, lineTotalHT);
    totalTaxAmount = addMoney(totalTaxAmount, lineTaxAmount);
    totalLineDiscountAmount = addMoney(
      totalLineDiscountAmount,
      lineDiscountAmount
    );
  });

  const totalTTC = addMoney(totalHT, totalTaxAmount);

  return {
    totalHT: parsePrice(totalHT), // internal 4-decimals
    totalTaxAmount: parsePrice(totalTaxAmount),
    totalDiscountAmount: parsePrice(totalLineDiscountAmount),
    totalTTC: parsePrice(totalTTC),
  };
};

/* ------------------------------
 * 🔹 PAYMENTS & BALANCE
 * ------------------------------ */

export const calculatePaymentBalance = (totalTTC, payments = []) => {
  const total = parsePrice(totalTTC);
  const totalPaid = payments.reduce(
    (sum, payment) => addMoney(sum, parsePrice(payment.amount || 0)),
    0
  );
  const remainingBalance = subMoney(total, totalPaid);

  return {
    totalPaid: parsePrice(totalPaid),
    remainingBalance: parsePrice(remainingBalance),
  };
};

/* ------------------------------
 * 🔹 Ticket & BALANCE
 * ------------------------------ */

export const calculateTicketBalance = (totalTTC, payments = []) => {
  const total = parsePrice(totalTTC);
  const totalPaid = payments.reduce(
    (sum, payment) => addMoney(sum, parsePrice(payment.paymentAmountIncludingTax || 0)),
    0
  );
  const remainingBalance = subMoney(total, totalPaid);

  return {
    totalPaid: parsePrice(totalPaid),
    remainingBalance: parsePrice(remainingBalance),
  };
};

export const isFullyPaid = (totalTTC, payments = []) => {
  const { remainingBalance } = calculatePaymentBalance(totalTTC, payments);
  return remainingBalance <= 0;
};

/* ------------------------------
 * 🔹 QUICK TEST UTILS (OPTIONAL)
 * ------------------------------ */

// Optional helper you can run in console to quickly check the example you showed:
export const _debugExample = () => {
  const puHT = 51.402; // exemple
  const qty = 10;
  const taxRate = 7;

  const totalHT = calculateLineTotalHT(qty, puHT, 0); // internal 4-dec
  const taxAmount = calculateLineTaxAmount(totalHT, taxRate); // internal 4-dec
  const totalTTC_internal = calculateLineTotalTTC(totalHT, taxAmount); // internal 4-dec
  const totalTTC_display = formatPrice(totalTTC_internal); // shows 3-dec

  console.log({ totalHT, taxAmount, totalTTC_internal, totalTTC_display });
};



